import { useState, useEffect, useCallback } from 'react';

interface OfflineState {
    isOnline: boolean;
    queuedOperations: unknown[];
    isSyncing: boolean;
}

export const useOfflineSync = () => {
    const [state, setState] = useState<OfflineState>({
        isOnline: navigator.onLine,
        queuedOperations: [],
        isSyncing: false
    });

    // Handle network status changes and trigger auto-sync
    useEffect(() => {
        const handleOnline = () => {
            setState(prev => ({ ...prev, isOnline: true }));

            // Notify service worker that we're online for auto-sync
            if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: 'CLIENT_ONLINE' });
            }
        };

        const handleOffline = () => {
            setState(prev => ({ ...prev, isOnline: false }));
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Listen for service worker messages about queue status
    useEffect(() => {
        if (!navigator.serviceWorker) return;

        const handleMessage = (event: MessageEvent) => {
            const { data } = event;

            if (data.type === 'REQUEST_QUEUED') {
                setState(prev => ({
                    ...prev,
                    queuedOperations: Array(data.queueLength).fill({}) // Simple representation
                }));
            } else if (data.type === 'SMART_SYNC_COMPLETE') {
                setState(prev => ({
                    ...prev,
                    queuedOperations: Array(data.remainingCount).fill({}),
                    isSyncing: false
                }));

                // Trigger smart update event with detailed sync info
                window.dispatchEvent(new CustomEvent('smart-sync-complete', {
                    detail: {
                        syncedRequests: data.syncedRequests,
                        summary: data.summary,
                        remainingCount: data.remainingCount
                    }
                }));
            } else if (data.type === 'SMART_SYNC_FAILURE') {
                setState(prev => ({
                    ...prev,
                    queuedOperations: Array(data.remainingCount ?? 0).fill({}),
                    isSyncing: false,
                }));
            } else if (data.type === 'PENDING_SYNC_FAILURES_AVAILABLE') {
                setState(prev => ({
                    ...prev,
                    queuedOperations: Array(data.remainingCount ?? 0).fill({}),
                    isSyncing: false,
                }));
            } else if (data.type === 'SYNC_COMPLETE') {
                // This handles the simple sync complete from the basic SW
                // To make it compatible with useSmartSync, we create a similar event structure.
                window.dispatchEvent(new CustomEvent('smart-sync-complete', {
                    detail: {
                        syncedRequests: data.payload.results || [],
                        summary: {
                            affectedLists: [...new Set((data.payload.results || []).map((r: { url: string }) => r.url.match(/shopping-lists\/([a-zA-Z0-9-]+)/)?.[1]).filter(Boolean))],
                            itemsAdded: (data.payload.results || []).filter((r: { success: boolean }) => r.success).length || 0,
                            itemsToggled: 0,
                            itemsDeleted: 0,
                        }
                    }
                }));
            }
        };

        // Always call addEventListener directly
        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
        };
    }, []);

    // Get current queue status from service worker
    const getQueueStatus = useCallback(async () => {
        if (!navigator.serviceWorker || !navigator.serviceWorker.controller) return;

        return new Promise<{ queueLength: number; queue: unknown[] }>((resolve) => {
            const messageChannel = new MessageChannel();
            messageChannel.port1.onmessage = (event) => {
                const { queueLength, queue } = event.data;
                setState(prev => ({
                    ...prev,
                    queuedOperations: Array(queueLength).fill({})
                }));
                resolve({ queueLength, queue });
            };

            navigator.serviceWorker.controller!.postMessage(
                { type: 'GET_QUEUE_STATUS' },
                [messageChannel.port2]
            );

            // Timeout after 2 seconds
            setTimeout(() => resolve({ queueLength: 0, queue: [] }), 2000);
        });
    }, []);

    // Trigger manual sync
    const startSync = useCallback(() => {
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            setState(prev => ({ ...prev, isSyncing: true }));
            navigator.serviceWorker.controller.postMessage({ type: 'PROCESS_QUEUE' });
        }
    }, []);

    // Initialize queue status on mount
    useEffect(() => {
        getQueueStatus();
    }, [getQueueStatus]);

    return {
        isOnline: state.isOnline,
        queuedOperations: state.queuedOperations,
        isSyncing: state.isSyncing,
        startSync,
        getQueueStatus
    };
};
